/**
 * Task Canonical Mapper
 *
 * Pure function that transforms form data → Firestore write model.
 * No side effects, no service imports, no Firestore calls.
 *
 * @see docs/TASK_CANONICAL_CONTRACT_V1.md §8
 */

import { omitNullish } from '@/features/shared/mapperUtils';

// ============================================================================
// Types
// ============================================================================

/**
 * Allowed createdVia values for tasks.
 * Must include 'smartadd' and 'import' per PROV-5.
 */
export type TaskCreatedVia =
  | 'tasks_tab'
  | 'quick_add'
  | 'task_editor'
  | 'note_promotion'
  | 'work_order_sidebar'
  | 'appointment_followup'
  | 'invoice_reminder'
  | 'dashboard'
  | 'smartadd'
  | 'import'
  | 'seed';

export interface TaskWriteModelOptions {
  mode?: 'create' | 'update';
  createdVia?: TaskCreatedVia;
  createdViaRefId?: string;
}

// ============================================================================
// Defaults (create-only)
// ============================================================================

const CREATE_DEFAULTS = {
  status: 'open' as const,
  scope: 'personal' as const,
  tags: [] as string[],
};

// ============================================================================
// Mapper
// ============================================================================

/**
 * Transform task form data into a Firestore write model.
 *
 * - **Create mode**: applies canonical defaults, sets provenance
 * - **Update mode**: passes through fields, no defaults, no provenance
 * - **Both modes**: trims title, computes searchTitle, applies omitNullish()
 * - **Does NOT**: mutate referenced entities, compute domain data
 */
export function toTaskWriteModel(
  data: Record<string, unknown>,
  options: TaskWriteModelOptions = {}
): Record<string, unknown> {
  const { mode = 'create', createdVia, createdViaRefId } = options;
  const isCreate = mode === 'create';

  // Trim string fields
  const title = typeof data.title === 'string' ? data.title.trim() : data.title;

  const result: Record<string, unknown> = {
    ...data,
    title,
  };

  // Apply defaults on create only (MAP-3)
  if (isCreate) {
    if (!result.status) result.status = CREATE_DEFAULTS.status;
    if (!result.scope) result.scope = CREATE_DEFAULTS.scope;
    if (!result.tags || !Array.isArray(result.tags)) result.tags = CREATE_DEFAULTS.tags;
  }

  // Provenance — create only (PROV-1)
  if (isCreate && createdVia) {
    result.createdVia = createdVia;
    if (createdViaRefId) {
      result.createdViaRefId = createdViaRefId;
    }
  }

  // Compute search fields (SVC-3)
  if (title && typeof title === 'string') {
    result.searchTitle = title.toLowerCase();
  }

  return omitNullish(result);
}
